import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, getDoc, writeBatch } from 'firebase/firestore';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth, googleProvider } from '../firebase/config';
import './Admin.css';

const ADMIN_EMAILS = [
  'drew.kordula@gmail.com',
];

// ── Delete all subcollections under a user ──────────────────────────
async function deleteUserData(email) {
  const batch = writeBatch(db);
  const userRef = doc(db, 'users', email);

  // Get all kids
  const kidsSnap = await getDocs(collection(db, 'users', email, 'kids'));
  for (const kidDoc of kidsSnap.docs) {
    const kidId = kidDoc.id;
    // Delete scannedTeams and their games
    const teamsSnap = await getDocs(collection(db, 'users', email, 'kids', kidId, 'scannedTeams'));
    for (const teamDoc of teamsSnap.docs) {
      const gamesSnap = await getDocs(collection(db, 'users', email, 'kids', kidId, 'scannedTeams', teamDoc.id, 'games'));
      gamesSnap.docs.forEach(g => batch.delete(g.ref));
      batch.delete(teamDoc.ref);
    }
    // Delete scannedEvents
    const eventsSnap = await getDocs(collection(db, 'users', email, 'kids', kidId, 'scannedEvents'));
    eventsSnap.docs.forEach(e => batch.delete(e.ref));
    batch.delete(kidDoc.ref);
  }
  batch.delete(userRef);
  await batch.commit();
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('teams');
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) loadData();
  }, [user, tab]);

  const loadData = async () => {
    setDataLoading(true);
    setSelected(null);
    setEditMode(false);
    try {
      if (tab === 'teams') {
        const snap = await getDocs(query(collection(db, 'teams'), orderBy('createdAt', 'desc')));
        setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else if (tab === 'events') {
        const snap = await getDocs(query(collection(db, 'events'), orderBy('createdAt', 'desc')));
        setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else if (tab === 'users') {
        const snap = await getDocs(collection(db, 'users'));
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDataLoading(false);
    }
  };

  const loadUserDetail = async (email) => {
    setUserDetailLoading(true);
    try {
      const kidsSnap = await getDocs(collection(db, 'users', email, 'kids'));
      const kids = [];
      for (const kidDoc of kidsSnap.docs) {
        const teamsSnap = await getDocs(collection(db, 'users', email, 'kids', kidDoc.id, 'scannedTeams'));
        const eventsSnap = await getDocs(collection(db, 'users', email, 'kids', kidDoc.id, 'scannedEvents'));
        kids.push({
          id: kidDoc.id,
          name: kidDoc.data().name,
          teamsCount: teamsSnap.size,
          eventsCount: eventsSnap.size,
        });
      }
      setUserDetail(kids);
    } catch (err) {
      console.error(err);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleApprove = async (item) => {
    const col = tab === 'teams' ? 'teams' : 'events';
    await updateDoc(doc(db, col, item.id), { approved: true });
    loadData();
  };

  const handleDelete = async (item) => {
    const label = tab === 'teams' ? 'team' : tab === 'events' ? 'event' : 'user';
    if (!window.confirm(`Delete "${item.name || item.email}"? This cannot be undone.`)) return;
    if (tab === 'users') {
      await deleteUserData(item.email || item.id);
    } else {
      const col = tab === 'teams' ? 'teams' : 'events';
      await deleteDoc(doc(db, col, item.id));
    }
    loadData();
  };

  const handleEditSave = async () => {
    try {
      if (tab === 'teams' || tab === 'events') {
        const col = tab === 'teams' ? 'teams' : 'events';
        await updateDoc(doc(db, col, selected.id), {
          name: editForm.name,
          city: editForm.city,
          state: editForm.state,
          sport: editForm.sport,
          website: editForm.website || '',
          facebook: editForm.facebook || '',
          instagram: editForm.instagram || '',
        });
      } else if (tab === 'users') {
        await updateDoc(doc(db, 'users', selected.email || selected.id), {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          city: editForm.city,
          state: editForm.state,
        });
      }
      setEditMode(false);
      loadData();
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
  };

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') return;
      alert('Sign in failed: ' + err.message);
    }
  };

  const handleLogout = () => signOut(auth);

  if (loading) return <div className="admin-loading"><div className="admin-spinner" /></div>;

  if (!user) return (
    <div className="admin-login">
      <div className="admin-login__card">
        <div className="admin-login__logo">
          <img src="/logo.png" alt="The Patch Guys" style={{height:'64px', width:'auto'}} />
        </div>
        <h1 className="admin-login__title">Admin Portal</h1>
        <p className="admin-login__subtitle">Sign in with your Google account to manage registrations.</p>
        <button className="admin-login__btn" onClick={handleLogin}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Sign in with Google
        </button>
      </div>
    </div>
  );

  if (!ADMIN_EMAILS.includes(user.email)) return (
    <div className="admin-login">
      <div className="admin-login__card">
        <div style={{fontSize:'48px', marginBottom:'16px'}}>🔒</div>
        <h1 className="admin-login__title">Access Denied</h1>
        <p className="admin-login__subtitle">Your account ({user.email}) is not authorized.</p>
        <button className="btn btn-secondary" onClick={handleLogout}>Sign Out</button>
      </div>
    </div>
  );

  const items = tab === 'teams' ? teams : tab === 'events' ? events : users;
  const filtered = tab === 'users' ? items : items.filter(item => {
    if (filter === 'pending')  return !item.approved;
    if (filter === 'approved') return  item.approved;
    return true;
  });

  const pendingTeams  = teams.filter(t => !t.approved).length;
  const pendingEvents = events.filter(e => !e.approved).length;

  return (
    <div className="admin">
      {/* Sidebar */}
      <aside className="admin__sidebar">
        <div className="admin__sidebar-logo">
          <div className="admin__logo-icon">TPG</div>
          <span>Admin</span>
        </div>
        <nav className="admin__nav">
          <button className={`admin__nav-item ${tab === 'teams' ? 'admin__nav-item--active' : ''}`}
            onClick={() => { setTab('teams'); setFilter('pending'); }}>
            🛡️ Teams
            {pendingTeams > 0 && <span className="admin__badge admin__badge--pending">{pendingTeams}</span>}
          </button>
          <button className={`admin__nav-item ${tab === 'events' ? 'admin__nav-item--active' : ''}`}
            onClick={() => { setTab('events'); setFilter('pending'); }}>
            🎫 Events
            {pendingEvents > 0 && <span className="admin__badge admin__badge--pending">{pendingEvents}</span>}
          </button>
          <button className={`admin__nav-item ${tab === 'users' ? 'admin__nav-item--active' : ''}`}
            onClick={() => { setTab('users'); setFilter('all'); }}>
            👤 Users
            <span className="admin__badge" style={{background:'#1A6EDB'}}>{users.length || ''}</span>
          </button>
        </nav>
        <div className="admin__sidebar-user">
          <img src={user.photoURL || ''} alt={user.displayName} className="admin__user-avatar" />
          <div className="admin__user-info">
            <div className="admin__user-name">{user.displayName}</div>
            <div className="admin__user-email">{user.email}</div>
          </div>
          <button className="admin__logout" onClick={handleLogout} title="Sign out">⇥</button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin__main">
        <div className="admin__header">
          <div>
            <h1 className="admin__title">
              {tab === 'teams' ? '🛡️ Teams' : tab === 'events' ? '🎫 Events' : '👤 Users'}
            </h1>
            <p className="admin__subtitle">
              {tab === 'users' ? `${filtered.length} total users` : `${filtered.length} ${filter} registration${filtered.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="admin__filter-tabs">
            {tab !== 'users' && ['pending','approved','all'].map(f => (
              <button key={f}
                className={`admin__filter-tab ${filter === f ? 'admin__filter-tab--active' : ''}`}
                onClick={() => setFilter(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'pending' && tab === 'teams'  && pendingTeams  > 0 && ` (${pendingTeams})`}
                {f === 'pending' && tab === 'events' && pendingEvents > 0 && ` (${pendingEvents})`}
              </button>
            ))}
            <button className="admin__refresh-btn" onClick={loadData} title="Refresh">↺</button>
          </div>
        </div>

        {dataLoading
          ? <div className="admin__loading"><div className="admin-spinner" /></div>
          : filtered.length === 0
            ? <div className="admin__empty">
                <div className="admin__empty-icon">✅</div>
                <h3>All clear!</h3>
                <p>No {filter} {tab} at the moment.</p>
              </div>
            : <div className="admin__grid">
                {/* List */}
                <div className="admin__list">
                  {filtered.map(item => (
                    <div key={item.id}
                      className={`admin__list-item ${selected?.id === item.id ? 'admin__list-item--active' : ''} ${item.approved ? 'admin__list-item--approved' : ''}`}
                      onClick={() => {
                        setSelected(item);
                        setEditMode(false);
                        setEditForm(item);
                        setUserDetail(null);
                        if (tab === 'users') loadUserDetail(item.email || item.id);
                      }}>
                      {tab !== 'users'
                        ? item.logoUrl
                          ? <img src={item.logoUrl} alt={item.name} className="admin__list-logo" />
                          : <div className="admin__list-logo admin__list-logo--placeholder">{item.name?.[0]}</div>
                        : <div className="admin__list-logo admin__list-logo--placeholder" style={{background:'#1A6EDB'}}>
                            {(item.firstName?.[0] || item.email?.[0] || '?').toUpperCase()}
                          </div>
                      }
                      <div className="admin__list-info">
                        <div className="admin__list-name">
                          {tab === 'users' ? `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email : item.name}
                        </div>
                        <div className="admin__list-meta">
                          {tab === 'users'
                            ? `${item.email} · ${item.accountType || 'unknown'}`
                            : `${item.city}, ${item.state} · ${item.sport}`}
                        </div>
                      </div>
                      {tab !== 'users' && (
                        <div className={`admin__list-status ${item.approved ? 'admin__list-status--approved' : 'admin__list-status--pending'}`}>
                          {item.approved ? 'Approved' : 'Pending'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Detail panel */}
                {selected && (
                  <div className="admin__detail">
                    {editMode ? (
                      // ── Edit form ──────────────────────────────
                      <div>
                        <h2 className="admin__detail-name" style={{marginBottom:'20px'}}>
                          ✏️ Edit {tab === 'users' ? 'User' : tab === 'teams' ? 'Team' : 'Event'}
                        </h2>
                        {tab === 'users' ? (
                          <>
                            <div className="admin__edit-field">
                              <label>First Name</label>
                              <input value={editForm.firstName || ''} onChange={e => setEditForm(f => ({...f, firstName: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>Last Name</label>
                              <input value={editForm.lastName || ''} onChange={e => setEditForm(f => ({...f, lastName: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>City</label>
                              <input value={editForm.city || ''} onChange={e => setEditForm(f => ({...f, city: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>State</label>
                              <input value={editForm.state || ''} onChange={e => setEditForm(f => ({...f, state: e.target.value}))} />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="admin__edit-field">
                              <label>Name</label>
                              <input value={editForm.name || ''} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>City</label>
                              <input value={editForm.city || ''} onChange={e => setEditForm(f => ({...f, city: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>State</label>
                              <input value={editForm.state || ''} onChange={e => setEditForm(f => ({...f, state: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>Sport</label>
                              <input value={editForm.sport || ''} onChange={e => setEditForm(f => ({...f, sport: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>Website</label>
                              <input value={editForm.website || ''} onChange={e => setEditForm(f => ({...f, website: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>Facebook</label>
                              <input value={editForm.facebook || ''} onChange={e => setEditForm(f => ({...f, facebook: e.target.value}))} />
                            </div>
                            <div className="admin__edit-field">
                              <label>Instagram</label>
                              <input value={editForm.instagram || ''} onChange={e => setEditForm(f => ({...f, instagram: e.target.value}))} />
                            </div>
                          </>
                        )}
                        <div className="admin__detail-actions">
                          <button className="btn admin__approve-btn" onClick={handleEditSave}>💾 Save</button>
                          <button className="btn admin__reject-btn" style={{background:'#374151'}} onClick={() => setEditMode(false)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      // ── Detail view ────────────────────────────
                      <>
                        {tab === 'users' ? (
                          <>
                            <div className="admin__detail-header">
                              <div className="admin__detail-logo admin__detail-logo--placeholder" style={{background:'#1A6EDB', fontSize:'28px'}}>
                                {(selected.firstName?.[0] || selected.email?.[0] || '?').toUpperCase()}
                              </div>
                              <div>
                                <h2 className="admin__detail-name">{selected.firstName} {selected.lastName}</h2>
                                <div className="admin__detail-meta">{selected.email}</div>
                                <div className="admin__detail-meta">{selected.city}, {selected.state} · {selected.accountType || 'unknown'}</div>
                              </div>
                            </div>
                            <div className="admin__detail-section">
                              <div className="admin__detail-label">Kids & Patches</div>
                              {userDetailLoading
                                ? <div style={{color:'#9ca3af', fontSize:'13px'}}>Loading...</div>
                                : userDetail && userDetail.length === 0
                                  ? <div style={{color:'#9ca3af', fontSize:'13px'}}>No kids added yet</div>
                                  : userDetail?.map(kid => (
                                      <div key={kid.id} className="admin__kid-row">
                                        {kid.editMode
                                          ? <div style={{display:'flex', gap:'8px', flex:1, alignItems:'center'}}>
                                              <input
                                                value={kid.editName ?? kid.name}
                                                onChange={e => setUserDetail(prev => prev.map(k => k.id === kid.id ? {...k, editName: e.target.value} : k))}
                                                className="admin__kid-input"
                                              />
                                              <button className="admin__kid-btn admin__kid-btn--save"
                                                onClick={async () => {
                                                  await updateDoc(doc(db, 'users', selected.email || selected.id, 'kids', kid.id), { name: kid.editName ?? kid.name });
                                                  setUserDetail(prev => prev.map(k => k.id === kid.id ? {...k, name: kid.editName ?? kid.name, editMode: false} : k));
                                                }}>Save</button>
                                              <button className="admin__kid-btn admin__kid-btn--cancel"
                                                onClick={() => setUserDetail(prev => prev.map(k => k.id === kid.id ? {...k, editMode: false} : k))}>✕</button>
                                            </div>
                                          : <>
                                              <div style={{flex:1}}>
                                                <div className="admin__kid-name">{kid.name}</div>
                                                <div className="admin__kid-meta">
                                                  {kid.teamsCount} team{kid.teamsCount !== 1 ? 's' : ''} · {kid.eventsCount} event{kid.eventsCount !== 1 ? 's' : ''}
                                                </div>
                                              </div>
                                              <button className="admin__kid-btn admin__kid-btn--edit"
                                                onClick={() => setUserDetail(prev => prev.map(k => k.id === kid.id ? {...k, editMode: true, editName: k.name} : k))}>✏️</button>
                                              <button className="admin__kid-btn admin__kid-btn--delete"
                                                onClick={async () => {
                                                  if (!window.confirm(`Delete kid "${kid.name}" and all their patch data?`)) return;
                                                  const kidRef = doc(db, 'users', selected.email || selected.id, 'kids', kid.id);
                                                  const teamsSnap = await getDocs(collection(db, 'users', selected.email || selected.id, 'kids', kid.id, 'scannedTeams'));
                                                  const batch = writeBatch(db);
                                                  for (const t of teamsSnap.docs) {
                                                    const gSnap = await getDocs(collection(db, 'users', selected.email || selected.id, 'kids', kid.id, 'scannedTeams', t.id, 'games'));
                                                    gSnap.docs.forEach(g => batch.delete(g.ref));
                                                    batch.delete(t.ref);
                                                  }
                                                  const evSnap = await getDocs(collection(db, 'users', selected.email || selected.id, 'kids', kid.id, 'scannedEvents'));
                                                  evSnap.docs.forEach(e => batch.delete(e.ref));
                                                  batch.delete(kidRef);
                                                  await batch.commit();
                                                  setUserDetail(prev => prev.filter(k => k.id !== kid.id));
                                                }}>🗑</button>
                                            </>
                                        }
                                      </div>
                                    ))
                              }
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="admin__detail-header">
                              {selected.logoUrl
                                ? <img src={selected.logoUrl} alt={selected.name} className="admin__detail-logo" />
                                : <div className="admin__detail-logo admin__detail-logo--placeholder">{selected.name?.[0]}</div>
                              }
                              <div>
                                <h2 className="admin__detail-name">{selected.name}</h2>
                                <div className="admin__detail-meta">{selected.city}, {selected.state} · {selected.sport}</div>
                                <div className={`admin__detail-status ${selected.approved ? 'admin__detail-status--approved' : 'admin__detail-status--pending'}`}>
                                  {selected.approved ? '✓ Approved' : '⏳ Pending Review'}
                                </div>
                              </div>
                            </div>
                            <div className="admin__detail-section">
                              <div className="admin__detail-label">Contact</div>
                              <div className="admin__detail-value">{selected.contactFirstName} {selected.contactLastName}</div>
                              <div className="admin__detail-value">
                                <a href={`mailto:${selected.contactEmail}`}>{selected.contactEmail}</a>
                              </div>
                            </div>
                            {tab === 'events' && selected.startDate && (
                              <div className="admin__detail-section">
                                <div className="admin__detail-label">Event Dates</div>
                                <div className="admin__detail-value">
                                  {new Date(selected.startDate.seconds * 1000).toLocaleDateString()} –{' '}
                                  {selected.endDate ? new Date(selected.endDate.seconds * 1000).toLocaleDateString() : 'TBD'}
                                </div>
                              </div>
                            )}
                            {(selected.website || selected.facebook || selected.instagram) && (
                              <div className="admin__detail-section">
                                <div className="admin__detail-label">Social / Web</div>
                                {selected.website   && <a href={selected.website}   target="_blank" rel="noopener noreferrer" className="admin__detail-link">🌐 {selected.website}</a>}
                                {selected.facebook  && <a href={selected.facebook}  target="_blank" rel="noopener noreferrer" className="admin__detail-link">📘 {selected.facebook}</a>}
                                {selected.instagram && <a href={selected.instagram} target="_blank" rel="noopener noreferrer" className="admin__detail-link">📸 {selected.instagram}</a>}
                              </div>
                            )}
                            <div className="admin__detail-section">
                              <div className="admin__detail-label">QR Code</div>
                              <div className="admin__detail-value admin__detail-mono">{selected.qrCode}</div>
                            </div>
                            <div className="admin__detail-section">
                              <div className="admin__detail-label">Submitted</div>
                              <div className="admin__detail-value">
                                {selected.createdAt ? new Date(selected.createdAt.seconds * 1000).toLocaleString() : 'Unknown'}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="admin__detail-actions">
                          {tab !== 'users' && !selected.approved && (
                            <button className="btn admin__approve-btn" onClick={() => handleApprove(selected)}>
                              ✓ Approve
                            </button>
                          )}
                          <button className="btn admin__edit-btn" onClick={() => { setEditMode(true); setEditForm(selected); }}>
                            ✏️ Edit
                          </button>
                          <button className="btn admin__reject-btn" onClick={() => handleDelete(selected)}>
                            🗑 Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
        }
      </main>
    </div>
  );
}